/**
 * Saved filter tabs, shown as segments: a rail down the left of the list.
 *
 * A segment is a filter tab. Same storage (`user_filter_tabs`, through
 * `/api/v1/me/filter-tabs`), same ids, same behaviour: picking one restores
 * its filters, and the unsaved dot means the table shows something the
 * segment has not saved. What the rail adds is a live count beside each name,
 * from one scan for all of them (see useSegmentCounts).
 *
 * Below the person's own segments ("Mine") are the ones shared with them
 * ("Shared"), each with its owner's name. A shared segment is filters only:
 * its count, like the list it opens, runs as whoever is looking.
 *
 * On a narrow screen a 210px rail would leave the table no room, so the same
 * actions collapse into a select and a menu above the filters.
 */
import {
  ActionIcon, Box, Button, Group, Menu, Select, Stack, Text, Tooltip, UnstyledButton,
} from '@mantine/core';
import {
  IconCopy, IconDeviceFloppy, IconDots, IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand,
  IconPencil, IconPlus, IconShare, IconTrash, IconUsers, IconUsersPlus,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { FilterTab, SegmentCounts, SharedSegment } from '../api/queries';
import { SHARED_PREFIX, sharedKey } from '../api/queries';
import { FONTS, TOKENS, formatNumber } from '../theme';
import { MonoLabel, SpeedChip } from '../ui';
import type { ShareTarget } from './ShareSegmentModal';
import { EnrolledByMenuItems } from './workflows/UsedByPanel';
import { useVocab, type Vocab } from '../vocab';

export const RAIL_WIDTH = 210;
const RAIL_COLLAPSED = 40;

export type SegmentActions = {
  onSelect: (id: number) => void;
  onNew: () => void;
  onSaveAs: () => void;
  onRename: (tab: FilterTab) => void;
  onDelete: (tab: FilterTab) => void;
};

/** What the rail can do with shared segments. Optional, so the rail still works without them. */
export type SharedSegmentActions = {
  onSelectShared: (id: string) => void;
  onShare: (target: ShareTarget) => void;
  onSaveCopy: (segment: SharedSegment) => void;
  onRenameShared: (segment: SharedSegment) => void;
  onDeleteShared: (segment: SharedSegment) => void;
  /** Enrol the segment's people into a workflow (for those who may manage workflows). */
  onEnrol?: (target: ShareTarget) => void;
};

type Props = SegmentActions & Partial<SharedSegmentActions> & {
  tabs: FilterTab[];
  /** The active private tab, unless a shared segment is open. */
  activeId: number | undefined;
  /** The dirty dot belongs to whichever segment is open. */
  dirty: boolean;
  counts: SegmentCounts | undefined;
  shared?: SharedSegment[];
  sharedCounts?: Map<string, number>;
  /** The open shared segment, which takes over from the private one. */
  activeSharedId?: string;
  /** This person may share (not a buyer or supplier login). */
  canShare?: boolean;
};

/**
 * A count, or a quiet ellipsis while it loads or if the count failed. With
 * people counted too, both: rows, then the distinct people among them.
 */
function countText(counts: SegmentCounts | undefined, id: number): string {
  const n = counts?.totals.get(id);
  if (n === undefined) return '…';
  const p = counts?.people?.get(id);
  return p === undefined ? formatNumber(n) : `${formatNumber(n)} · ${formatNumber(p)}`;
}

/** The same, in words, for a tooltip and a screen reader. */
function countTitle(counts: SegmentCounts | undefined, id: number, v: Vocab): string | undefined {
  const n = counts?.totals.get(id);
  const p = counts?.people?.get(id);
  if (n === undefined || p === undefined) return undefined;
  return `${v.count(n, 'lead')} · ${v.count(p, 'person')}`;
}

function sharedCountText(counts: Map<string, number> | undefined, id: string): string {
  const n = counts?.get(id);
  return n === undefined ? '…' : formatNumber(n);
}

function UnsavedDot({ label }: { label: string }) {
  return (
    <Tooltip label={label} withArrow>
      <Box
        w={7}
        h={7}
        aria-label={label}
        style={{ borderRadius: '50%', background: 'var(--mantine-color-chartAmber-6)', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

const countStyle = { fontFamily: FONTS.mono, fontSize: '0.6875rem', fontVariantNumeric: 'tabular-nums' } as const;

/** Share, rename and delete, for the row's menu and the narrow layout's menu alike. */
function SegmentMenuItems({ tab, only, canShare, onRename, onDelete, onShare, onEnrol }: {
  tab: FilterTab;
  only: boolean;
  canShare?: boolean;
} & Pick<SegmentActions, 'onRename' | 'onDelete'> & Partial<Pick<SharedSegmentActions, 'onShare' | 'onEnrol'>>) {
  const v = useVocab();
  return (
    <>
      {onEnrol && (
        <Menu.Item leftSection={<IconUsersPlus size={14} />} onClick={() => onEnrol({ kind: 'private', tab })}>
          {`Enrol in ${v.a('workflow')}…`}
        </Menu.Item>
      )}
      {canShare && onShare && (
        <Menu.Item leftSection={<IconShare size={14} />} onClick={() => onShare({ kind: 'private', tab })}>
          Share…
        </Menu.Item>
      )}
      <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => onRename(tab)}>
        Rename
      </Menu.Item>
      <Menu.Item
        leftSection={<IconTrash size={14} />}
        color="red"
        disabled={only}
        onClick={() => onDelete(tab)}
      >
        {only ? 'Delete (last segment)' : 'Delete segment'}
      </Menu.Item>
      <EnrolledByMenuItems refType="segment" refId={String(tab.id)} />
    </>
  );
}

/** A shared segment's menu: a copy for anyone, the rest for its owner or an admin. */
function SharedMenuItems({ segment, actions }: { segment: SharedSegment; actions: Partial<SharedSegmentActions> }) {
  const v = useVocab();
  return (
    <>
      {actions.onEnrol && (
        <Menu.Item leftSection={<IconUsersPlus size={14} />} onClick={() => actions.onEnrol?.({ kind: 'shared', segment })}>
          {`Enrol in ${v.a('workflow')}…`}
        </Menu.Item>
      )}
      {actions.onSaveCopy && (
        <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => actions.onSaveCopy?.(segment)}>
          Save a copy
        </Menu.Item>
      )}
      {segment.can_edit && (
        <>
          <Menu.Item leftSection={<IconShare size={14} />} onClick={() => actions.onShare?.({ kind: 'shared', segment })}>
            Share…
          </Menu.Item>
          <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => actions.onRenameShared?.(segment)}>
            Rename
          </Menu.Item>
          <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => actions.onDeleteShared?.(segment)}>
            Delete for everyone
          </Menu.Item>
        </>
      )}
      <EnrolledByMenuItems refType="shared_segment" refId={segment.id} />
    </>
  );
}

/** Who shared it, for the rail and the select. */
export function sharedByLine(s: SharedSegment): string {
  return s.is_owner ? 'Shared by you' : `Shared by ${s.owner_name}`;
}

function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          mr={2}
          className="hf-segment-more"
          aria-label={label}
        >
          <IconDots size={13} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>{children}</Menu.Dropdown>
    </Menu>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Text
      size="xs"
      c="dimmed"
      fw={600}
      px={8}
      pt={6}
      pb={2}
      style={{ letterSpacing: '0.02em' }}
    >
      {children}
    </Text>
  );
}

export function SegmentsRail({
  tabs, activeId, dirty, counts, collapsed, onToggle, onSelect, onNew, onSaveAs, onRename, onDelete,
  shared = [], sharedCounts, activeSharedId, canShare, ...sharedActions
}: Props & { collapsed: boolean; onToggle: () => void }) {
  const v = useVocab();
  if (collapsed) {
    return (
      <Stack
        gap={6}
        align="center"
        py={6}
        style={{ width: RAIL_COLLAPSED, flexShrink: 0 }}
        data-testid="segments-rail"
      >
        <Tooltip label="Show segments" position="right" withArrow>
          <ActionIcon variant="subtle" color="gray" onClick={onToggle} aria-label="Show segments">
            <IconLayoutSidebarLeftExpand size={17} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="New segment" position="right" withArrow>
          <ActionIcon variant="default" onClick={onNew} aria-label="New segment">
            <IconPlus size={15} />
          </ActionIcon>
        </Tooltip>
      </Stack>
    );
  }

  const showShared = shared.length > 0 || Boolean(canShare && sharedActions.onShare);

  return (
    <Box
      component="nav"
      aria-label="Segments"
      data-testid="segments-rail"
      style={{
        width: RAIL_WIDTH,
        flexShrink: 0,
        background: TOKENS.surface,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 'var(--mantine-radius-md)',
        position: 'sticky',
        top: 8,
      }}
    >
      <Group justify="space-between" px={10} pt={8} pb={4} wrap="nowrap">
        <MonoLabel>Segments</MonoLabel>
        <Group gap={2} wrap="nowrap">
          <Tooltip label="New segment" withArrow>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onNew} aria-label="New segment">
              <IconPlus size={15} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Hide segments" withArrow>
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onToggle} aria-label="Hide segments">
              <IconLayoutSidebarLeftCollapse size={15} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Stack gap={1} px={4} pb={4} style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
        {showShared && <SectionLabel>Mine</SectionLabel>}
        <Stack gap={1} role="list" aria-label="My segments" data-testid="segments-mine">
          {tabs.map((t) => {
            const active = activeSharedId === undefined && t.id === activeId;
            const unsaved = active ? dirty : t.has_changes;
            return (
              <Group
                key={t.id}
                role="listitem"
                gap={0}
                wrap="nowrap"
                className="hf-segment-row"
                data-active={active || undefined}
                style={{
                  borderRadius: 'var(--mantine-radius-sm)',
                  background: active ? 'var(--mantine-color-default-hover)' : undefined,
                }}
              >
                <UnstyledButton
                  onClick={() => onSelect(t.id)}
                  // Double-click renames, as the tab strip did.
                  onDoubleClick={() => onRename(t)}
                  aria-current={active ? 'true' : undefined}
                  style={{ flex: 1, minWidth: 0, padding: '6px 4px 6px 8px' }}
                >
                  <Group gap={6} wrap="nowrap">
                    <Text
                      size="sm"
                      fw={active ? 600 : 500}
                      truncate="end"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      {t.name}
                    </Text>
                    {unsaved && <UnsavedDot label="Unsaved filter changes" />}
                    <Text component="span" c="dimmed" style={countStyle} title={countTitle(counts, t.id, v)}>
                      {countText(counts, t.id)}
                    </Text>
                  </Group>
                </UnstyledButton>
                <RowMenu label={`Options for ${t.name}`}>
                  <SegmentMenuItems
                    tab={t}
                    only={tabs.length <= 1}
                    canShare={canShare}
                    onRename={onRename}
                    onDelete={onDelete}
                    onShare={sharedActions.onShare}
                    onEnrol={sharedActions.onEnrol}
                  />
                </RowMenu>
              </Group>
            );
          })}
        </Stack>

        {showShared && (
          <>
            <SectionLabel>Shared</SectionLabel>
            <Stack gap={1} role="list" aria-label="Shared segments" data-testid="segments-shared">
              {shared.length === 0 && (
                <Text size="xs" c="dimmed" px={8} pb={4}>
                  Nothing shared yet. Share a segment from its menu.
                </Text>
              )}
              {shared.map((s) => {
                const active = s.id === activeSharedId;
                return (
                  <Group
                    key={s.id}
                    role="listitem"
                    gap={0}
                    wrap="nowrap"
                    className="hf-segment-row"
                    data-active={active || undefined}
                    style={{
                      borderRadius: 'var(--mantine-radius-sm)',
                      background: active ? 'var(--mantine-color-default-hover)' : undefined,
                    }}
                  >
                    <UnstyledButton
                      onClick={() => sharedActions.onSelectShared?.(s.id)}
                      aria-current={active ? 'true' : undefined}
                      // The rail truncates; the full name is a hover away.
                      title={s.name}
                      style={{ flex: 1, minWidth: 0, padding: '5px 4px 5px 8px' }}
                    >
                      <Group gap={6} wrap="nowrap">
                        <IconUsers size={13} color={TOKENS.textMuted} style={{ flexShrink: 0 }} aria-hidden />
                        <Text
                          size="sm"
                          fw={active ? 600 : 500}
                          truncate="end"
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          {s.name}
                        </Text>
                        {active && dirty && <UnsavedDot label="Unsaved filter changes" />}
                        <Text component="span" c="dimmed" style={countStyle}>
                          {sharedCountText(sharedCounts, s.id)}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" truncate="end" pl={19}>
                        {sharedByLine(s)}
                      </Text>
                    </UnstyledButton>
                    <RowMenu label={`Options for ${s.name}`}>
                      <SharedMenuItems segment={s} actions={sharedActions} />
                    </RowMenu>
                  </Group>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>

      <Stack gap={6} px={8} pb={8} pt={4} style={{ borderTop: `1px solid ${TOKENS.border}` }}>
        <Button
          size="compact-xs"
          variant={dirty ? 'light' : 'subtle'}
          leftSection={<IconDeviceFloppy size={13} />}
          onClick={onSaveAs}
          disabled={!dirty}
          fullWidth
        >
          Save as segment
        </Button>
        <SpeedChip stats={counts?.query_stats} style={{ justifyContent: 'center' }} />
      </Stack>
    </Box>
  );
}

/** The narrow-screen form: a select to switch, a menu for the rest. */
export function SegmentsSelect({
  tabs, activeId, dirty, counts, onSelect, onNew, onSaveAs, onRename, onDelete,
  shared = [], sharedCounts, activeSharedId, canShare, ...sharedActions
}: Props) {
  const activeShared = shared.find((s) => s.id === activeSharedId);
  const active = activeShared ? undefined : tabs.find((t) => t.id === activeId);
  const mine = tabs.map((t) => ({ value: String(t.id), label: `${t.name} (${countText(counts, t.id)})` }));
  const value = activeShared ? sharedKey(activeShared.id) : activeId !== undefined ? String(activeId) : null;
  return (
    <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }} data-testid="segments-select">
      <Select
        size="xs"
        aria-label="Segment"
        value={value}
        onChange={(v) => {
          if (!v) return;
          if (v.startsWith(SHARED_PREFIX)) sharedActions.onSelectShared?.(v.slice(SHARED_PREFIX.length));
          else onSelect(Number(v));
        }}
        data={shared.length === 0 ? mine : [
          { group: 'Mine', items: mine },
          {
            group: 'Shared',
            items: shared.map((s) => ({
              value: sharedKey(s.id),
              label: `${s.name} (${sharedCountText(sharedCounts, s.id)})`,
            })),
          },
        ]}
        allowDeselect={false}
        style={{ flex: 1, minWidth: 0 }}
        rightSection={dirty ? <UnsavedDot label="Unsaved filter changes" /> : undefined}
      />
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="default" aria-label="Segment options">
            <IconDots size={15} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconPlus size={14} />} onClick={onNew}>New segment</Menu.Item>
          <Menu.Item leftSection={<IconDeviceFloppy size={14} />} disabled={!dirty} onClick={onSaveAs}>
            Save as segment
          </Menu.Item>
          {active && (
            <>
              <Menu.Divider />
              <SegmentMenuItems
                tab={active}
                only={tabs.length <= 1}
                canShare={canShare}
                onRename={onRename}
                onDelete={onDelete}
                onShare={sharedActions.onShare}
                onEnrol={sharedActions.onEnrol}
              />
            </>
          )}
          {activeShared && (
            <>
              <Menu.Divider />
              <Menu.Label>{sharedByLine(activeShared)}</Menu.Label>
              <SharedMenuItems segment={activeShared} actions={sharedActions} />
            </>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
